import React from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { Heart, MessageCircle, Send, Star, Users, Zap, CheckCircle, Mic, Radio, Search } from 'lucide-react'

/**
 * Hero mock — Engagement Queue card showing a post with a pre-written comment
 */
export function HeroMock(): JSX.Element {
  return (
    <Box sx={{ maxWidth: 420, mx: 'auto', mt: 4 }}>
      <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', overflow: 'hidden' }}>
        {/* Post header */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip label="📷 Instagram" size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'white', fontSize: '11px' }} />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ color: 'rgba(168,85,247,0.7)', fontSize: '11px' }}>87% match</Typography>
        </Box>
        {/* Author */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #f09433, #dc2743)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '14px' }}>S</Typography>
          </Box>
          <Box>
            <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>@sarahpodcasts</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>2 hours ago</Typography>
          </Box>
        </Box>
        {/* Caption */}
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.5 }}>
            Just wrapped recording our episode on building authentic community as a podcaster. The key insight? Stop broadcasting, start conversing...
          </Typography>
        </Box>
        {/* Metrics */}
        <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Heart size={12} color="rgba(255,255,255,0.4)" /><Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>342</Typography></Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><MessageCircle size={12} color="rgba(255,255,255,0.4)" /><Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>28</Typography></Box>
        </Box>
        {/* Comment section */}
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(168,85,247,0.03)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>AI-generated comment</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', lineHeight: 1.5, mb: 1.5, fontStyle: 'italic' }}>
            This really resonates. We explored the same shift on our show last week, going from monologue to dialogue changed everything for our listener engagement.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1, py: 0.75, borderRadius: '8px', background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Send size={12} color="white" />
              <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>Reply</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '11px', textAlign: 'center', mt: 1.5 }}>3 conversations left &nbsp;·&nbsp; 8 joined today</Typography>
    </Box>
  )
}

/**
 * Discover tab mock — Social Radar signals
 */
export function DiscoverMock(): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
      {[
        { name: 'True Crime Storytelling', platforms: ['📷', '🎵', '🔴'], posts: 24, status: 'Active' },
        { name: 'Mentions: My Crime Podcast', platforms: ['📷', '🐦', '🔴'], posts: 7, status: 'Active' },
        { name: 'EP: The Cold Case Files', platforms: ['📷', '🎵'], posts: 15, status: 'Scanning...' },
      ].map((signal) => (
        <Box key={signal.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: 'linear-gradient(135deg, #a855f7, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Radio size={14} color="white" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{signal.name}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
              {signal.platforms.map((p, i) => <Typography key={i} sx={{ fontSize: '10px' }}>{p}</Typography>)}
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ color: '#a855f7', fontSize: '12px', fontWeight: 700 }}>{signal.posts}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px' }}>posts</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

/**
 * Engage tab mock — Queue with session stats
 */
export function EngageMock(): JSX.Element {
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>12 conversations left</Typography>
        <Typography sx={{ color: '#a855f7', fontSize: '11px', fontWeight: 600 }}>8 joined today</Typography>
      </Box>
      <Box sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #00f2ea, #ff0050)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 700 }}>M</Typography>
          </Box>
          <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>@mindsetcoach_</Typography>
          <Chip label="🎵 TikTok" size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)', fontSize: '10px', height: 20, ml: 'auto' }} />
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', lineHeight: 1.5, mb: 1.5 }}>
          Your morning routine is everything. Here are 5 habits that changed my life...
        </Typography>
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(168,85,247,0.05)', borderRadius: '8px', border: '1px solid rgba(168,85,247,0.1)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontStyle: 'italic' }}>
            The morning journal habit is so underrated. We just did a deep dive on this with a neuroscientist on our show.
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Network tab mock — Profile cards with guest tag
 */
export function NetworkMock(): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
      {[
        { name: '@wellness_daily', followers: '12.5K', engaged: 4, guest: true },
        { name: '@podcast_growth', followers: '8.2K', engaged: 2, guest: true },
        { name: '@mindful_media', followers: '3.1K', engaged: 1, guest: false },
      ].map((profile) => (
        <Box key={profile.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(168,85,247,0.3), rgba(99,102,241,0.3))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#a855f7', fontSize: '13px', fontWeight: 700 }}>{profile.name.charAt(1).toUpperCase()}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>{profile.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}><Users size={10} color="rgba(255,255,255,0.4)" /><Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>{profile.followers}</Typography></Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Chip icon={<CheckCircle size={10} />} label={`Engaged ${String(profile.engaged)}x`} size="small" sx={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: '10px', height: 20, '& .MuiChip-icon': { color: '#22c55e' } }} />
            {profile.guest && <Chip icon={<Mic size={9} />} label="Potential Guest" size="small" sx={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontSize: '9px', height: 18, '& .MuiChip-icon': { color: '#f59e0b' } }} />}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

/**
 * Promote tab mock — Episode promotion flow
 */
export function PromoteMock(): JSX.Element {
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, p: 2, backgroundColor: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.15)', borderRadius: '12px' }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '10px', background: 'linear-gradient(135deg, #a855f7, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Mic size={20} color="white" />
        </Box>
        <Box>
          <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 600, mb: 0.25 }}>EP: The Psychology of Habit Formation</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', mb: 1 }}>Released Apr 10 · 42 min</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Search size={11} color="rgba(255,255,255,0.4)" />
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>18 conversations found</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Zap size={11} color="#a855f7" />
              <Typography sx={{ fontSize: '11px', color: '#a855f7', fontWeight: 600 }}>6 engaged</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Analyze tab mock — Audience insights preview
 */
export function AnalyzeMock(): JSX.Element {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 2 }}>
      <Box sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>Top Platform</Typography>
        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>📷 Instagram</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>45% of your audience</Typography>
      </Box>
      <Box sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>Podcast Rating</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Star size={16} color="#f59e0b" fill="#f59e0b" />
          <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '18px' }}>4.7</Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>128 ratings</Typography>
      </Box>
      <Box sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', gridColumn: 'span 2' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>Audience Topics</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {['mindfulness', 'habits', 'psychology', 'productivity', 'wellness'].map((t) => (
            <Chip key={t} label={t} size="small" sx={{ backgroundColor: 'rgba(168,85,247,0.1)', color: '#a855f7', fontSize: '10px', height: 20 }} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Step mocks for How it Works
 */
export function StepOneMock(): JSX.Element {
  return (
    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', display: 'flex', gap: 1.5, alignItems: 'center' }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '10px', overflow: 'hidden', background: 'linear-gradient(135deg, #a855f7, #ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Mic size={18} color="white" />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>The Mindful Podcast</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>3 signals created · Scanning...</Typography>
      </Box>
      <CheckCircle size={16} color="#22c55e" />
    </Box>
  )
}

export function StepTwoMock(): JSX.Element {
  return (
    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>5 left</Typography>
        <Typography sx={{ color: '#a855f7', fontSize: '10px', fontWeight: 600 }}>7 joined</Typography>
      </Box>
      <Box sx={{ height: 6, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: '58%', height: '100%', background: 'linear-gradient(135deg, #a855f7, #6366f1)', borderRadius: 3 }} />
      </Box>
    </Box>
  )
}

export function StepThreeMock(): JSX.Element {
  return (
    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
      <Box sx={{ flex: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', textAlign: 'center' }}>
        <Typography sx={{ color: '#22c55e', fontWeight: 700, fontSize: '16px' }}>+34</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>New followers</Typography>
      </Box>
      <Box sx={{ flex: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', textAlign: 'center' }}>
        <Typography sx={{ color: '#a855f7', fontWeight: 700, fontSize: '16px' }}>12</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>Potential guests</Typography>
      </Box>
      <Box sx={{ flex: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', textAlign: 'center' }}>
        <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '16px' }}>7d</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>Streak</Typography>
      </Box>
    </Box>
  )
}
